import * as React from 'react';

const FAQSection: React.FC = () => {
  // State to track which FAQ is expanded
  const [expandedFaq, setExpandedFaq] = React.useState<number | null>(null);

  // FAQ data
  const faqs = [
    {
      id: 1,
      question: "How does RoastMyListing.ai analyze my Amazon product listing?",
      answer: "Our AI-powered tool analyzes multiple aspects of your Amazon listing including your title, bullet points, description, images, pricing, and reviews. It evaluates your listing against Amazon best practices to identify specific areas for improvement. The analysis is delivered with a humorous, no-nonsense approach that makes the feedback memorable and actionable. While our AI provides valuable insights, remember that this tool is meant to be both helpful AND entertaining!"
    },
    {
      id: 2,
      question: "Is the analysis guaranteed to improve my listing?",
      answer: "While our AI provides valuable insights based on e-commerce best practices, the analysis is not guaranteed to improve your sales or rankings. RoastMyListing.ai is designed to be both educational and entertaining - we combine practical advice with humor to make optimization more enjoyable. Results may vary based on your specific product, market conditions, and how you implement the suggestions. Think of us as a fun second opinion, not a magic sales bullet!"
    },
    {
      id: 3,
      question: "How accurate is the AI analysis?",
      answer: "Our AI analysis combines e-commerce best practices with computational humor to provide insights that are both useful and entertaining. While we strive for accuracy, the tool is designed to spark ideas and highlight potential issues rather than provide definitive answers. The 'roast' format is intentionally exaggerated for entertainment value, so take the specific wording with a grain of salt, but do consider the underlying recommendations!"
    },
    {
      id: 4,
      question: "Can I use RoastMyListing.ai for products other than Amazon listings?",
      answer: "RoastMyListing.ai is specifically designed for Amazon product listings. While some of the general e-commerce principles might apply to other platforms, our analysis is tailored to Amazon's unique algorithm, customer behavior patterns, and best practices. We may expand to other platforms in the future, but for now, we're focused on helping Amazon sellers improve their listings with a smile!"
    },
    {
      id: 5,
      question: "Is this service really free?",
      answer: "Yes! Our basic Amazon listing analysis is completely free. We believe in providing genuine value upfront. We offer premium services for sellers who want more in-depth optimization support, but our core roasting service will always remain free to use with no hidden fees or obligations."
    },
    {
      id: 6,
      question: "How do I implement the suggestions from my listing roast?",
      answer: "After receiving your roast, review the suggestions and prioritize them based on what's easiest to implement and what might have the biggest impact. You can make changes directly in your Amazon Seller Central account. Remember that some changes (like title updates) may need approval from Amazon, and it's best to make changes incrementally rather than overhauling everything at once. And don't forget - while we bring the humor, you should use your own judgment about which suggestions make sense for your specific product and brand!"
    }
  ];

  // Toggle FAQ expansion
  const toggleFaq = (id: number) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  return (
    <div style={{
      padding: '70px 0',
      backgroundColor: '#f5f3ff', /* Changed to a light purple background */
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.05,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234f46e5' fill-opacity='0.4' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E")`,
      }} />
      
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
        padding: '0 20px'
      }}>
        <h2 style={{ 
          fontSize: '2.5rem', 
          fontWeight: 'bold', 
          marginBottom: '16px', 
          color: '#1f2937',
          textAlign: 'center',
          background: 'linear-gradient(to right, #4f46e5, #7c3aed)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          display: 'inline-block',
          margin: '0 auto 48px',
          width: '100%'
        }}>
          Frequently Asked Questions
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {faqs.map(faq => (
            <div 
              key={faq.id} 
              style={{ 
                backgroundColor: 'white', 
                borderRadius: '12px', 
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
                border: '1px solid rgba(79, 70, 229, 0.1)',
                overflow: 'hidden',
                transition: 'all 0.3s ease'
              }}
            >
              <div 
                style={{ 
                  padding: '20px 24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  backgroundColor: expandedFaq === faq.id ? 'rgba(79, 70, 229, 0.03)' : 'white',
                  borderBottom: expandedFaq === faq.id ? '1px solid rgba(79, 70, 229, 0.1)' : 'none'
                }}
                onClick={() => toggleFaq(faq.id)}
              >
                <h3 style={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 'bold', 
                  color: '#1f2937',
                  margin: 0
                }}>
                  {faq.question}
                </h3>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: expandedFaq === faq.id ? '#4f46e5' : '#e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'transform 0.3s ease, background-color 0.3s ease',
                  transform: expandedFaq === faq.id ? 'rotate(45deg)' : 'rotate(0deg)'
                }}>
                  <span style={{
                    color: expandedFaq === faq.id ? 'white' : '#6b7280',
                    fontSize: '18px',
                    lineHeight: '1',
                    fontWeight: 'bold',
                    marginTop: '-3px'
                  }}>+</span>
                </div>
              </div>
              
              {expandedFaq === faq.id && (
                <div style={{ 
                  padding: '20px 24px',
                  color: '#4b5563',
                  fontSize: '1rem',
                  lineHeight: '1.7',
                  borderTop: '1px solid #f3f4f6'
                }}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQSection;
